import React, { useState, useEffect } from 'react';
import {
  CalendarIcon,
  ClockIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import { ukSchedulingAPI, employeeAPI, timeAttendanceAPI, organizationAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const UKScheduling = () => {
  const [activeTab, setActiveTab] = useState('rotas');
  const [rotas, setRotas] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [complianceViolations, setComplianceViolations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Date range for viewing
  const [dateRange, setDateRange] = useState({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(new Date().setDate(new Date().getDate() + 14)).toISOString().split('T')[0]
  });

  // Rota form state
  const [rotaForm, setRotaForm] = useState({
    name: '',
    site_id: '',
    week_start_date: new Date().toISOString().split('T')[0],
    description: '',
    status: 'draft'
  });

  // Shift form state
  const [shiftForm, setShiftForm] = useState({
    employee_id: '',
    site_id: '',
    rota_id: '',
    shift_date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '17:00',
    break_duration: 60,
    role: '',
    notes: ''
  });

  const [showRotaModal, setShowRotaModal] = useState(false);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [editingRota, setEditingRota] = useState(null);
  const [selectedRota, setSelectedRota] = useState(null);

  const { user, isAdmin, isManager, isHRManager } = useAuth();

  // Tab configuration
  const tabs = [
    {
      id: 'rotas',
      name: 'Weekly Rotas',
      icon: CalendarIcon,
      description: 'Manage weekly work rotas'
    },
    {
      id: 'shifts',
      name: 'Shift Management',
      icon: ClockIcon,
      description: 'Assign and manage individual shifts'
    },
    {
      id: 'sites',
      name: 'Sites',
      icon: BuildingOffice2Icon,
      description: 'Manage workplace sites'
    },
    {
      id: 'compliance',
      name: 'Compliance Check',
      icon: ShieldCheckIcon,
      description: 'UK employment law compliance'
    }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'rotas') {
      fetchRotas();
    } else if (activeTab === 'shifts') {
      fetchShifts();
    } else if (activeTab === 'compliance') {
      fetchComplianceViolations();
    }
  }, [dateRange, activeTab]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [employeesResponse, companiesResponse] = await Promise.all([
        employeeAPI.getAll({}),
        organizationAPI.getCompanies()
      ]);
      
      setEmployees(employeesResponse.data.employees || []);
      // Extract sites from companies data or use a dedicated sites endpoint
      const allSites = companiesResponse.data?.companies?.reduce((acc, company) => {
        return acc.concat(company.sites || []);
      }, []) || [];
      setSites(allSites);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRotas = async () => {
    try {
      setLoading(true);
      const response = await ukSchedulingAPI.getRotas({
        date_from: dateRange.start_date,
        date_to: dateRange.end_date
      });
      setRotas(response.data.rotas || []);
    } catch (error) {
      console.error('Error fetching rotas:', error);
      setError('Failed to load rotas');
    } finally {
      setLoading(false);
    }
  };

  const fetchShifts = async () => {
    try {
      setLoading(true);
      const response = await ukSchedulingAPI.getShifts({
        date_from: dateRange.start_date,
        date_to: dateRange.end_date
      });
      setShifts(response.data.shifts || []);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      setError('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  const fetchComplianceViolations = async () => {
    try {
      setLoading(true);
      const response = await ukSchedulingAPI.getDashboard({
        date_from: dateRange.start_date,
        date_to: dateRange.end_date,
        company_id: user.companyId
      });
      setComplianceViolations(response.data.dashboard?.compliance_issues || []);
    } catch (error) {
      console.error('Error fetching compliance violations:', error);
      setError('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRota = async () => {
    try {
      setLoading(true);
      const rotaData = {
        ...rotaForm,
        company_id: user.companyId,
        period_start: rotaForm.week_start_date,
        period_end: new Date(new Date(rotaForm.week_start_date).setDate(new Date(rotaForm.week_start_date).getDate() + 6)).toISOString().split('T')[0]
      };
      
      await ukSchedulingAPI.createRota(rotaData);
      
      setShowRotaModal(false);
      setRotaForm({
        name: '',
        site_id: '',
        week_start_date: new Date().toISOString().split('T')[0],
        description: '',
        status: 'draft'
      });
      
      fetchRotas();
    } catch (error) {
      console.error('Error creating rota:', error);
      setError('Failed to create rota');
    } finally {
      setLoading(false);
    }
  };

  const handlePublishRota = async (rotaId) => {
    if (!window.confirm('Are you sure you want to publish this rota? Employees will be notified of any compliance issues will be validated.')) {
      return;
    }

    try {
      const response = await ukSchedulingAPI.publishRota(rotaId);
      if (response.data.success) {
        fetchRotas();
      } else if (response.data.compliance_issues) {
        setError(`Cannot publish rota: ${response.data.compliance_issues.join(', ')}`);
      }
    } catch (error) {
      console.error('Error publishing rota:', error);
      setError('Failed to publish rota: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCreateShift = async () => {
    try {
      setLoading(true);
      
      // Check compliance before creating
      const complianceResponse = await ukSchedulingAPI.validateAction({
        action_type: 'assign_shift',
        entity_data: {
          employee_id: shiftForm.employee_id,
          shift_date: shiftForm.shift_date,
          start_time: shiftForm.start_time,
          end_time: shiftForm.end_time,
          break_duration: shiftForm.break_duration
        }
      });

      if (complianceResponse.data.violations && complianceResponse.data.violations.length > 0) {
        const criticalViolations = complianceResponse.data.violations.filter(v => v.severity === 'error');
        if (criticalViolations.length > 0) {
          const proceed = window.confirm(
            `Critical compliance violations detected:\n${criticalViolations.map(v => v.message).join('\n')}\n\nDo you want to proceed anyway?`
          );
          if (!proceed) {
            setLoading(false);
            return;
          }
        }
      }

      await ukSchedulingAPI.createShift({
        ...shiftForm,
        start_ts: `${shiftForm.shift_date}T${shiftForm.start_time}:00`,
        end_ts: `${shiftForm.shift_date}T${shiftForm.end_time}:00`,
        break_minutes: shiftForm.break_duration
      });
      
      setShowShiftModal(false);
      setShiftForm({
        employee_id: '',
        site_id: '',
        rota_id: '',
        shift_date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '17:00',
        break_duration: 60,
        role: '',
        notes: ''
      });
      
      fetchShifts();
    } catch (error) {
      console.error('Error creating shift:', error);
      setError('Failed to create shift');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      published: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || colors.draft;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      error: 'bg-red-100 text-red-800 border-red-200',
      warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      info: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[severity] || colors.info;
  };

  const renderRotasTab = () => {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Weekly Rotas</h3>
          {(isAdmin() || isManager() || isHRManager()) && (
            <button
              onClick={() => setShowRotaModal(true)}
              className="btn-primary"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Rota
            </button>
          )}
        </div>

        {/* Date Range Controls */}
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Week Starting
              </label>
              <input
                type="date"
                className="form-input"
                value={dateRange.start_date}
                onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  const today = new Date();
                  const monday = new Date(today.setDate(today.getDate() - today.getDay() + 1));
                  setDateRange({
                    start_date: monday.toISOString().split('T')[0],
                    end_date: new Date(monday.setDate(monday.getDate() + 13)).toISOString().split('T')[0]
                  });
                }}
                className="btn-secondary w-full"
              >
                This Week
              </button>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  const today = new Date();
                  const nextMonday = new Date(today.setDate(today.getDate() - today.getDay() + 8));
                  setDateRange({
                    start_date: nextMonday.toISOString().split('T')[0],
                    end_date: new Date(nextMonday.setDate(nextMonday.getDate() + 13)).toISOString().split('T')[0]
                  });
                }}
                className="btn-secondary w-full"
              >
                Next Week
              </button>
            </div>
          </div>
        </div>

        {/* Rotas List */}
        {rotas.length === 0 ? (
          <div className="card text-center py-12">
            <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Rotas Found</h3>
            <p className="text-gray-600">Create your first weekly rota to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {rotas.map((rota) => (
              <div key={rota.id} className="card">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">{rota.name}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(rota.status)}`}>
                    {rota.status}
                  </span>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <BuildingOffice2Icon className="w-4 h-4 mr-2" />
                    <span>{rota.site_name || 'All Sites'}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    <span>Week of {formatDate(rota.week_start_date)}</span>
                  </div>
                  {rota.description && (
                    <p className="text-sm text-gray-600">{rota.description}</p>
                  )}
                </div>

                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    {rota.total_shifts || 0} shifts assigned
                  </div>
                  <div className="flex space-x-2">
                    {rota.status === 'draft' && (isAdmin() || isManager() || isHRManager()) && (
                      <button
                        onClick={() => handlePublishRota(rota.id)}
                        className="btn-secondary text-sm"
                      >
                        Publish
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedRota(rota)}
                      className="btn-secondary text-sm"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderShiftsTab = () => {
    // Group shifts by date for better visualization
    const shiftsByDate = shifts.reduce((acc, shift) => {
      const date = shift.shift_date.split('T')[0];
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(shift);
      return acc;
    }, {});

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Shift Management</h3>
          {(isAdmin() || isManager() || isHRManager()) && (
            <button
              onClick={() => setShowShiftModal(true)}
              className="btn-primary"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Assign Shift
            </button>
          )}
        </div>

        {/* Date Range Controls */}
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                className="form-input"
                value={dateRange.start_date}
                onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                className="form-input"
                value={dateRange.end_date}
                onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  const today = new Date();
                  setDateRange({
                    start_date: today.toISOString().split('T')[0],
                    end_date: new Date(today.setDate(today.getDate() + 7)).toISOString().split('T')[0]
                  });
                }}
                className="btn-secondary w-full"
              >
                Next 7 Days
              </button>
            </div>
          </div>
        </div>

        {/* Shifts by Date */}
        {Object.keys(shiftsByDate).length === 0 ? (
          <div className="card text-center py-12">
            <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Shifts Found</h3>
            <p className="text-gray-600">No shifts scheduled for the selected date range.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(shiftsByDate)
              .sort(([a], [b]) => new Date(a) - new Date(b))
              .map(([date, dateShifts]) => (
              <div key={date} className="card">
                <h4 className="text-lg font-medium text-gray-900 mb-4">
                  {formatDate(date)} ({dateShifts.length} shifts)
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dateShifts.map((shift) => (
                    <div key={shift.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">
                          {shift.employee_name}
                        </span>
                        {shift.compliance_status && (
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            shift.compliance_status === 'compliant' 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {shift.compliance_status}
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>Time:</span>
                          <span>{formatTime(shift.start_time)} - {formatTime(shift.end_time)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Break:</span>
                          <span>{shift.break_duration} min</span>
                        </div>
                        {shift.site_name && (
                          <div className="flex justify-between">
                            <span>Site:</span>
                            <span>{shift.site_name}</span>
                          </div>
                        )}
                        {shift.role && (
                          <div className="flex justify-between">
                            <span>Role:</span>
                            <span>{shift.role}</span>
                          </div>
                        )}
                      </div>
                      
                      {shift.notes && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs text-gray-600">{shift.notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSitesTab = () => {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Workplace Sites</h3>
          {(isAdmin() || isHRManager()) && (
            <button className="btn-primary">
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Site
            </button>
          )}
        </div>

        {/* Sites Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sites.map((site) => (
            <div key={site.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-lg font-medium text-gray-900">{site.name}</h4>
                  {site.description && (
                    <p className="text-sm text-gray-600 mt-1">{site.description}</p>
                  )}
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  site.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {site.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                {site.address && (
                  <div className="flex items-start">
                    <BuildingOffice2Icon className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{site.address}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Active Employees:</span>
                  <span className="font-medium">{site.employee_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Current Shifts:</span>
                  <span className="font-medium">{site.current_shifts || 0}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderComplianceTab = () => {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">UK Employment Law Compliance</h3>
            <p className="text-sm text-gray-600">Monitor Working Time Regulations and other compliance issues</p>
          </div>
          <button
            onClick={fetchComplianceViolations}
            className="btn-secondary"
          >
            Refresh Check
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card text-center">
            <div className="text-2xl font-bold text-red-600 mb-2">
              {complianceViolations.filter(v => v.severity === 'error').length}
            </div>
            <div className="text-sm text-gray-600">Critical Violations</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-yellow-600 mb-2">
              {complianceViolations.filter(v => v.severity === 'warning').length}
            </div>
            <div className="text-sm text-gray-600">Warnings</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-blue-600 mb-2">
              {complianceViolations.length}
            </div>
            <div className="text-sm text-gray-600">Total Issues</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-green-600 mb-2">
              {Math.max(0, 100 - (complianceViolations.length * 2))}%
            </div>
            <div className="text-sm text-gray-600">Compliance Score</div>
          </div>
        </div>

        {/* Violations List */}
        {complianceViolations.length === 0 ? (
          <div className="card text-center py-12">
            <ShieldCheckIcon className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">All Clear!</h3>
            <p className="text-gray-600">No compliance violations detected for the selected period.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {complianceViolations.map((violation, index) => (
              <div key={index} className={`card border-l-4 ${getSeverityColor(violation.severity)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <ExclamationTriangleIcon 
                        className={`w-5 h-5 mr-2 ${
                          violation.severity === 'error' ? 'text-red-500' : 'text-yellow-500'
                        }`} 
                      />
                      <h4 className="text-lg font-medium text-gray-900">{violation.rule_name}</h4>
                      <span className={`ml-auto px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(violation.severity).split(' ').slice(0, 2).join(' ')}`}>
                        {violation.severity}
                      </span>
                    </div>
                    
                    <p className="text-gray-700 mb-3">{violation.error_message}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Employee:</span> {violation.employee_name}
                      </div>
                      <div>
                        <span className="font-medium">Date:</span> {formatDate(violation.violation_date)}
                      </div>
                      <div>
                        <span className="font-medium">Category:</span> {violation.rule_category}
                      </div>
                    </div>
                    
                    {violation.legal_reference && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="text-sm text-blue-600">
                          <DocumentTextIcon className="w-4 h-4 inline mr-1" />
                          <a href={violation.legal_reference} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            Legal Reference
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'rotas':
        return renderRotasTab();
      case 'shifts':
        return renderShiftsTab();
      case 'sites':
        return renderSitesTab();
      case 'compliance':
        return renderComplianceTab();
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">UK Scheduling & Compliance</h1>
          <p className="text-sm text-gray-600 mt-1">Manage employee rotas with UK employment law compliance</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`nav-item py-4 px-1 border-b-2 font-medium flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'nav-item-active border-blue-500 text-blue-600'
                      : 'nav-item-inactive border-transparent'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            renderTabContent()
          )}
        </div>
      </div>

      {/* Modals */}
      {showRotaModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Create Weekly Rota
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rota Name
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={rotaForm.name}
                    onChange={(e) => setRotaForm({ ...rotaForm, name: e.target.value })}
                    placeholder="e.g., Week 23 - Main Site"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site
                  </label>
                  <select
                    className="form-input"
                    value={rotaForm.site_id}
                    onChange={(e) => setRotaForm({ ...rotaForm, site_id: e.target.value })}
                  >
                    <option value="">All Sites</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Week Starting (Monday)
                  </label>
                  <input
                    type="date"
                    className="form-input"
                    value={rotaForm.week_start_date}
                    onChange={(e) => setRotaForm({ ...rotaForm, week_start_date: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    className="form-input"
                    rows="3"
                    value={rotaForm.description}
                    onChange={(e) => setRotaForm({ ...rotaForm, description: e.target.value })}
                    placeholder="Optional description or notes..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowRotaModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRota}
                  disabled={!rotaForm.name}
                  className="btn-primary"
                >
                  Create Rota
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showShiftModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Assign Shift
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee
                  </label>
                  <select
                    className="form-input"
                    value={shiftForm.employee_id}
                    onChange={(e) => setShiftForm({ ...shiftForm, employee_id: e.target.value })}
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} ({emp.employee_number})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site
                  </label>
                  <select
                    className="form-input"
                    value={shiftForm.site_id}
                    onChange={(e) => setShiftForm({ ...shiftForm, site_id: e.target.value })}
                  >
                    <option value="">Select Site</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shift Date
                  </label>
                  <input
                    type="date"
                    className="form-input"
                    value={shiftForm.shift_date}
                    onChange={(e) => setShiftForm({ ...shiftForm, shift_date: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      className="form-input"
                      value={shiftForm.start_time}
                      onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time
                    </label>
                    <input
                      type="time"
                      className="form-input"
                      value={shiftForm.end_time}
                      onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Break Duration (minutes)
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={shiftForm.break_duration}
                    onChange={(e) => setShiftForm({ ...shiftForm, break_duration: parseInt(e.target.value) || 0 })}
                    min="0"
                    max="480"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role/Position
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={shiftForm.role}
                    onChange={(e) => setShiftForm({ ...shiftForm, role: e.target.value })}
                    placeholder="e.g., Cashier, Security, Manager"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    className="form-input"
                    rows="2"
                    value={shiftForm.notes}
                    onChange={(e) => setShiftForm({ ...shiftForm, notes: e.target.value })}
                    placeholder="Optional notes..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowShiftModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateShift}
                  disabled={!shiftForm.employee_id || !shiftForm.shift_date}
                  className="btn-primary"
                >
                  Assign Shift
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UKScheduling;